import { Form, useFormContext } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import {
    Stack,
    TextField,
    InputAdornment,
    IconButton
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import type { Cliente } from "@/shared/types/cliente";
import { getClienteDisplayValue } from "../../utils/de-data-utils";
import ClienteInfoCard from "../cliente-info-card";
import ClienteManualSearch from "@/features/facturacion-electronica/components/cliente-manual-search";

interface NotaRemisionGeneralInfoFieldsProps {
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
    clienteError?: boolean;
}

export default function NotaRemisionGeneralInfoFields({
    onClienteChange,
    cliente,
    clienteError = false,
}: NotaRemisionGeneralInfoFieldsProps) {
    const openModal = useOpenModal();
    const form = useFormContext();

    const handleClienteSelect = (c: Cliente) => {
        form.setFieldValue("clienteId", c.id ?? "");
        onClienteChange?.(c);
    };

    const handleClearCliente = () => {
        form.setFieldValue("clienteId", "");
        onClienteChange?.(null);
    };

    return (
        <CollapsibleSection title="Información General">
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="flex-start" flexWrap="wrap">
                <Stack flex={{ xs: '1 1 100%', sm: '2 1 350px' }} minWidth="280px">
                    <TextField
                        label="Cliente (Receptor) *"
                        size="small"
                        fullWidth
                        value={getClienteDisplayValue(cliente)}
                        placeholder="Buscar cliente..."
                        error={clienteError && !cliente}
                        helperText={clienteError && !cliente ? "Cliente es requerido" : ""}
                        slotProps={{
                            input: {
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {cliente && (
                                            <IconButton size="small" onClick={handleClearCliente}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton size="small" onClick={() => openModal("searchClienteNR")}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }
                        }}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => !cliente && openModal("searchClienteNR")}
                    />
                    <ClienteInfoCard cliente={cliente || null} title="Receptor" />
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '1 1 220px' }} minWidth="180px">
                    <Form.Select name="motivoEmisionNR" label="Motivo de Emisión *" size="small" slotProps={{ htmlInput: { sx: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, popover: { slotProps: { paper: { sx: { minWidth: 320 } } } } }}>
                        <Form.Select.Option value="1">Traslado por ventas</Form.Select.Option>
                        <Form.Select.Option value="2">Traslado por consignación</Form.Select.Option>
                        <Form.Select.Option value="3">Exportación</Form.Select.Option>
                        <Form.Select.Option value="4">Traslado por compra</Form.Select.Option>
                        <Form.Select.Option value="5">Importación</Form.Select.Option>
                        <Form.Select.Option value="6">Traslado por devolución</Form.Select.Option>
                        <Form.Select.Option value="7">Traslado entre locales de la empresa</Form.Select.Option>
                        <Form.Select.Option value="8">Traslado de bienes por transformación</Form.Select.Option>
                        <Form.Select.Option value="9">Traslado de bienes por reparación</Form.Select.Option>
                        <Form.Select.Option value="10">Traslado por emisor móvil</Form.Select.Option>
                        <Form.Select.Option value="11">Exhibición o Demostración</Form.Select.Option>
                        <Form.Select.Option value="12">Participación en ferias</Form.Select.Option>
                        <Form.Select.Option value="13">Traslado de encomienda</Form.Select.Option>
                        <Form.Select.Option value="14">Decomiso</Form.Select.Option>
                    </Form.Select>
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '1 1 220px' }} minWidth="180px">
                    <Form.Select
                        name="responsableEmision"
                        label="Responsable de Emisión *"
                        size="small"
                        slotProps={{ htmlInput: { sx: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, popover: { slotProps: { paper: { sx: { minWidth: 280 } } } } }}
                    >
                        <Form.Select.Option value="1">Emisor de la factura</Form.Select.Option>
                        <Form.Select.Option value="2">Poseedor de los bienes o mercaderías</Form.Select.Option>
                        <Form.Select.Option value="3">Empresa transportista</Form.Select.Option>
                        <Form.Select.Option value="4">Despachante de aduana</Form.Select.Option>
                        <Form.Select.Option value="5">Agente de transporte</Form.Select.Option>
                    </Form.Select>
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '0 1 150px' }} minWidth="120px">
                    <Form.NumberField
                        name="kmRecorrido"
                        label="Distancia (km) *"
                        size="small"
                        fullWidth
                    />
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '1 1 250px' }} minWidth="200px">
                    <Form.TextField name="observaciones" label="Observaciones" size="small" fullWidth />
                </Stack>
            </Stack>

            <Dialog code="searchClienteNR" size="lg">
                <ModalHeader>Buscar Cliente</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <ClienteManualSearch
                            selectedClienteId={cliente?.id}
                            onClienteSelect={(c: Cliente) => {
                                handleClienteSelect(c);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>
        </CollapsibleSection>
    );
}
